import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { IconButton, Stack, Tooltip } from '@mui/material'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import { Plus } from '@styled-icons/heroicons-outline/Plus'

import { get } from 'utils/lodash'
import { canUpdateCustomerManager } from 'data/permissions/permissionsSelectors'

import T from 'T'
import HHAccordion from 'components/common/HHAccordion'
import EventBlocker from 'components/common/EventBlocker'

import {
  closeAddBeneficialOwnerContact,
  openAddBeneficialOwnerContact,
  resetAddBeneficialOwnerContact,
  selectAddBeneficialOwnerContact,
  selectIsOpenAddBeneficialOwnerContact,
} from 'slices/customer/beneficialOwnerContacts'
import { useLazyFetchBeneficialOwnerContactsQuery } from 'api/contact/beneficialOwnersCrud'
import AddEditBeneficialOwnerDialog from 'components/customer-details/content/beneficial_owner_contacts/AddEditBeneficialOwnerDialog'
import ContactsListing from './ContactsListing'
import { getAddModel } from './settings'

const BeneficialOwnerContacts = ({ customerDetails }) => {
  const dispatch = useDispatch()
  const [fetchBeneficialOwnerContacts, { isFetching, isLoading, data }] = useLazyFetchBeneficialOwnerContactsQuery()
  const loading = useMemo(() => isFetching || isLoading, [isFetching, isLoading])
  const updateCustomerManager = useSelector(canUpdateCustomerManager, shallowEqual)
  const isOpenAddContactDialog = useSelector(selectIsOpenAddBeneficialOwnerContact, shallowEqual)
  const { initialMethodValue, initialFirstName, initialLastName } = useSelector(selectAddBeneficialOwnerContact, shallowEqual)
  const accountId = get(customerDetails, 'id')
  const contacts = get(data, 'beneficialOwnerContacts', [])
  const hasContacts = contacts.length > 0

  const [bulkExpansion, setBulkExpansion] = useState({ open: true, version: 0 })
  const toggleBulkExpansion = () => setBulkExpansion(state => ({ open: !state.open, version: state.version + 1 }))
  const collapseAllLabel = bulkExpansion.open ? T.COLLAPSE_ALL : T.EXPAND_ALL

  const addModel = getAddModel(initialFirstName, initialLastName, initialMethodValue)

  const handleOpenAddContactDialog = () => {
    dispatch(resetAddBeneficialOwnerContact())
    dispatch(openAddBeneficialOwnerContact())
  }

  const handleCloseAddContactDialog = () => {
    dispatch(resetAddBeneficialOwnerContact())
    dispatch(closeAddBeneficialOwnerContact())
  }

  useEffect(() => {
    fetchBeneficialOwnerContacts({
      accountId,
    })
  }, [])

  // Tight cluster of action icons on the right (mirrors the Customer Portal accordion
  // pattern): collapse/expand toggle immediately to the LEFT of the + (add) icon.
  // EventBlocker stops the click from toggling the accordion itself.
  const customActions = (
    <EventBlocker>
      <Stack direction="row" gap={0.5}>
        {hasContacts && (
          <Tooltip arrow title={collapseAllLabel}>
            <IconButton sx={{ color: 'text.secondary' }} onClick={toggleBulkExpansion} aria-label={collapseAllLabel}>
              {bulkExpansion.open ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </Tooltip>
        )}
        {updateCustomerManager && (
          <IconButton sx={{ color: 'text.secondary' }} onClick={handleOpenAddContactDialog} aria-label="Add beneficial owner contact">
            <Plus width={20} height={20} />
          </IconButton>
        )}
      </Stack>
    </EventBlocker>
  )

  return (
    <>
      <HHAccordion
        isExpandCollapseHeightSame
        withNewColors
        isExpanded
        summary={T.BENEFICIAL_OWNER_CONTACTS}
        showActionIcon={false}
        showCustomActions
        customActionContent={customActions}
        sx={{ position: 'inherit' }}
      >
        <ContactsListing
          loading={loading}
          contacts={contacts}
          customerDetails={customerDetails}
          showActionMenu
          bulkExpansion={bulkExpansion}
        />
      </HHAccordion>

      {isOpenAddContactDialog && (
        <AddEditBeneficialOwnerDialog
          isOpen={isOpenAddContactDialog}
          accountId={accountId}
          contact={initialMethodValue ? addModel : null}
          onClose={handleCloseAddContactDialog}
        />
      )}
    </>
  )
}

BeneficialOwnerContacts.propTypes = {
  customerDetails: PropTypes.object.isRequired,
}

export default BeneficialOwnerContacts
